/**
 * Screenshot Generator for Memory Sharing
 * Creates shareable images of memories for social media.
 *
 * Uses a fixed zoom level so the note has center stage: prominent on screen
 * with a good portion of background visible. Renders the existing MapLibre
 * sticker layer (same as the map) — no overlay.
 */

import maplibregl from 'maplibre-gl';
import { registerStickerSprites } from './stickerSprites';
import { logger } from './logger';

// OpenFreeMap base style
const BASE_STYLE_URL = 'https://tiles.openfreemap.org/styles/liberty';

// Screenshot format dimensions
export const SCREENSHOT_FORMATS = {
  PORTRAIT: { width: 1080, height: 1920, name: 'Stories' },   // Instagram/TikTok Stories
  SQUARE: { width: 1080, height: 1080, name: 'Square' },      // Instagram feed
  LANDSCAPE: { width: 1200, height: 630, name: 'Card' },      // Twitter/OG cards
};

// Default theme colors
const DEFAULT_COLORS = {
  background: '#FAF7F2',
  ocean: '#a8d4e6',
  land: '#f5f0e8',
  parks: '#d4e6c8',
  buildings: '#e8e0d5',
  roads: '#d4cfc6',
  borders: '#c5bbb0',
  labels: '#5a5a5a',
  romantic: '#E6B3C2',
  gold: '#F2C6A0',
};

/** Fixed camera: note center stage, good background. No slider. */
export const CAMERA_CONFIG = {
  zoom: 20,
  pitch: 60,
  bearing: 0,
};

/**
 * Note layout for export (memory-notes symbol layer).
 *
 * Map (Map) uses zoom-based interpolation:
 * - icon-size: [8→0.6, 10→0.75, 12→0.9, 14→1.1, 16→1.3]
 * - text-size: [8→6, 10→7, 12→8, 14→10, 16→12]
 * - text-max-width: 7 (ems) — MapLibre wraps lines at this width; ems scale with text-size.
 * - text-line-height: 1.1
 *
 * To avoid text overfitting the sticker:
 * 1. Derive text-size from icon-size (map ratio ~ text/icon ≈ 9 at z16).
 * 2. Use text-max-width in ems so wrapped lines stay within sticker width.
 * 3. Optionally truncate very long text (maxTextLength).
 */
export const NOTE_LAYOUT = {
  iconSize: 4.5,
  /** text-size = iconSize * textSizeRatio. Map ~9. */
  textSizeRatio: 9,
  /** Max line width in ems (map uses 7). Smaller = more wrap, fits sticker better. */
  textMaxWidthEms: 7,
  textLineHeight: 1.1,
  /** Truncate memory text to this length for export; 0 = no truncation. */
  maxTextLength: 200,
};

/**
 * Wait for map to become idle (tiles loaded, not moving)
 */
const waitForMapIdle = (map, timeout = 15000) => {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error('Map idle timeout'));
    }, timeout);

    const checkIdle = () => {
      if (!map.isMoving() && map.areTilesLoaded()) {
        clearTimeout(timer);
        resolve();
      } else {
        map.once('idle', () => {
          clearTimeout(timer);
          resolve();
        });
      }
    };

    checkIdle();
  });
};

/**
 * Apply theme colors to map style
 */
function applyThemeToStyle(style, themeColors) {
  const colors = themeColors || DEFAULT_COLORS;

  style.layers.forEach(layer => {
    if (!layer.paint) return;

    if (layer.id === 'background') {
      layer.paint['background-color'] = colors.land;
    }
    if (layer.id.includes('water') && layer.type === 'fill') {
      layer.paint['fill-color'] = colors.ocean;
    }
    if ((layer.id.includes('park') || layer.id.includes('forest') ||
         layer.id.includes('nature') || layer.id.includes('landcover')) &&
        layer.type === 'fill' && layer.paint['fill-color']) {
      layer.paint['fill-color'] = colors.parks;
    }
    if (layer.id.includes('road') && layer.type === 'line' && layer.paint['line-color']) {
      layer.paint['line-color'] = colors.roads;
    }
    if (layer.id.includes('building') && layer.type === 'fill' &&
        !layer.id.includes('3d') && layer.paint['fill-color']) {
      layer.paint['fill-color'] = colors.buildings;
    }
    if (layer.id.includes('border') && layer.type === 'line' && layer.paint['line-color']) {
      layer.paint['line-color'] = colors.borders;
    }
  });

  return style;
}

/**
 * GeoJSON for a single memory (center of map = note center stage).
 * Truncates text when NOTE_LAYOUT.maxTextLength > 0 to avoid overflow.
 */
function createMemoryGeoJSON(memory) {
  let text = (memory.text || '').trim() || 'A memory';
  const maxLen = NOTE_LAYOUT.maxTextLength;
  if (maxLen > 0 && text.length > maxLen) {
    text = text.slice(0, maxLen).trim() + '…';
  }
  return {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [memory.lng, memory.lat],
      },
      properties: {
        id: memory.id,
        text,
        color: memory.color || '#E6B3C2',
      },
    }],
  };
}

/**
 * Add branding overlay to canvas (bottom-right)
 */
function addBrandingOverlay(canvas, format) {
  const ctx = canvas.getContext('2d');
  const { width, height } = format;

  const gradientHeight = height * 0.12;
  const gradientWidth = width * 0.4;
  const gradient = ctx.createLinearGradient(
    width - gradientWidth, height - gradientHeight,
    width, height
  );
  gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
  gradient.addColorStop(1, 'rgba(0, 0, 0, 0.35)');

  ctx.fillStyle = gradient;
  ctx.fillRect(width - gradientWidth, height - gradientHeight, gradientWidth, gradientHeight);

  const fontSize = Math.max(14, Math.floor(width * 0.022));
  const padding = fontSize * 0.8;
  ctx.font = `600 ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
  ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'bottom';

  ctx.fillText('velvynote.app', width - padding, height - padding);
}

/**
 * Generate a screenshot of a memory.
 * Map is centered on the memory; note has center stage. Uses existing MapLibre sticker layer.
 *
 * @param {Object} options
 * @param {Object} options.memory - { id, lat, lng, text, color }
 * @param {string} options.format - 'PORTRAIT' | 'SQUARE' | 'LANDSCAPE'
 * @param {Object} options.theme - Optional { colors }
 * @param {Function} options.onProgress - Optional (0–100)
 * @returns {Promise<Blob>} PNG blob
 */
export async function generateScreenshot({
  memory,
  format = 'PORTRAIT',
  theme,
  onProgress = () => {},
}) {
  return new Promise(async (resolve, reject) => {
    let map = null;
    let container = null;

    const formatConfig = SCREENSHOT_FORMATS[format] || SCREENSHOT_FORMATS.PORTRAIT;

    try {
      onProgress(5);

      container = document.createElement('div');
      container.style.cssText = `
        position: fixed;
        left: 0;
        top: 0;
        width: ${formatConfig.width}px;
        height: ${formatConfig.height}px;
        opacity: 0;
        pointer-events: none;
        z-index: -9999;
      `;
      document.body.appendChild(container);

      const styleResponse = await fetch(BASE_STYLE_URL);
      let style = await styleResponse.json();

      delete style.sources.natural_earth;
      style.layers = style.layers.filter(l => l.source !== 'natural_earth');

      const poiKeywords = ['poi', 'amenity', 'transport', 'parking', 'toilet', 'bus', 'station', 'stop', 'shop', 'restaurant', 'cafe', 'fuel', 'atm', 'bank', 'pharmacy', 'hospital', 'school', 'university'];
      style.layers = style.layers.filter(layer => {
        const layerId = layer.id?.toLowerCase() || '';
        return !poiKeywords.some(keyword => layerId.includes(keyword));
      });

      style = applyThemeToStyle(style, theme?.colors);

      onProgress(20);

      map = new maplibregl.Map({
        container,
        style,
        center: [memory.lng, memory.lat],
        zoom: CAMERA_CONFIG.zoom,
        pitch: CAMERA_CONFIG.pitch,
        bearing: CAMERA_CONFIG.bearing,
        preserveDrawingBuffer: true,
        antialias: true,
      });

      await new Promise((res, rej) => {
        map.on('load', res);
        map.on('error', rej);
        setTimeout(() => rej(new Error('Map load timeout')), 30000);
      });

      onProgress(40);

      registerStickerSprites(map);

      map.addSource('memories', {
        type: 'geojson',
        data: createMemoryGeoJSON(memory),
      });

      const existingTextLayer = style.layers.find(l => l.layout && l.layout['text-font']);
      const availableFont = existingTextLayer?.layout?.['text-font']?.[0] || 'Noto Sans Regular';

      try {
        map.addLayer({
          id: 'buildings-3d',
          type: 'fill-extrusion',
          source: 'openmaptiles',
          'source-layer': 'building',
          minzoom: 14,
          paint: {
            'fill-extrusion-color': [
              'interpolate', ['linear'], ['coalesce', ['get', 'render_height'], 10],
              0, theme?.colors?.buildings || DEFAULT_COLORS.buildings,
              30, theme?.colors?.gold || DEFAULT_COLORS.gold,
              80, theme?.colors?.romantic || DEFAULT_COLORS.romantic
            ],
            'fill-extrusion-height': ['coalesce', ['get', 'render_height'], 10],
            'fill-extrusion-base': ['coalesce', ['get', 'render_min_height'], 0],
            'fill-extrusion-opacity': 0.8
          }
        });
      } catch (e) {
        logger.warn('Could not add 3D buildings:', e);
      }

      const iconSize = NOTE_LAYOUT.iconSize;
      const textSize = Math.round(iconSize * NOTE_LAYOUT.textSizeRatio);

      map.addLayer({
        id: 'memory-notes',
        type: 'symbol',
        source: 'memories',
        layout: {
          'icon-image': [
            'match',
            ['downcase', ['get', 'color']],
            '#e6b3c2', 'sticker-rose-medium',
            '#f2c6a0', 'sticker-peach-medium',
            '#c9d3b8', 'sticker-sage-medium',
            '#d4c4e0', 'sticker-lavender-medium',
            '#f4ede4', 'sticker-cream-medium',
            'sticker-rose-medium'
          ],
          'icon-size': iconSize,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'text-field': ['get', 'text'],
          'text-font': [availableFont],
          'text-size': textSize,
          'text-anchor': 'center',
          'text-justify': 'center',
          'text-max-width': NOTE_LAYOUT.textMaxWidthEms,
          'text-line-height': NOTE_LAYOUT.textLineHeight,
          'text-allow-overlap': true,
          'text-ignore-placement': true,
          'text-padding': 2,
        },
        paint: {
          'text-color': '#4A3F35',
        }
      });

      onProgress(60);

      await waitForMapIdle(map);
      await new Promise(r => setTimeout(r, 500));

      onProgress(80);

      const mapCanvas = map.getCanvas();
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = formatConfig.width;
      finalCanvas.height = formatConfig.height;
      const ctx = finalCanvas.getContext('2d');

      ctx.drawImage(mapCanvas, 0, 0, formatConfig.width, formatConfig.height);
      addBrandingOverlay(finalCanvas, formatConfig);

      onProgress(90);

      finalCanvas.toBlob((blob) => {
        cleanup();
        onProgress(100);
        resolve(blob);
      }, 'image/png', 1.0);

    } catch (error) {
      cleanup();
      reject(error);
    }

    function cleanup() {
      if (map) map.remove();
      if (container?.parentNode) container.parentNode.removeChild(container);
    }
  });
}

/**
 * Generate preview — same as exported image; returns data URL for <img>.
 */
export async function generatePreview({
  memory,
  format = 'PORTRAIT',
  theme,
}) {
  const blob = await generateScreenshot({
    memory,
    format,
    theme,
    onProgress: () => {},
  });
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error('Failed to read preview'));
    r.readAsDataURL(blob);
  });
}
